import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import Button from 'components/inputs/Button';
import Input from 'components/inputs/Input';
import LabelWrap from 'components/inputs/LabelWrap';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBolt,
  faClockRotateLeft,
  faFloppyDisk,
  faPlus,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  getPriceListGetActivePurchaseVersionQueryKey,
  getPriceListGetAllVersionsQueryKey,
  usePriceListActivateVersion,
  usePriceListGetActivePurchaseVersion,
  usePriceListGetAllVersions,
  usePriceListGetPurchaseVersion,
  usePriceListSavePurchaseVersion,
} from 'api/price-list/price-list';
import type { PurchasePriceListModel } from 'api/model';
import { PriceListGetAllVersionsType } from 'api/model';
import { showException } from 'utils/exception-helper';
import { formatDate, toDateOnlyString } from 'utils/date-helpers';
import ConfirmModal from 'components/Modal/ConfirmModal';
import useModalStack from 'contexts/modal/useModalStack';
import { SWEDISH_COUNTIES } from 'constants/AppConstants';

// #region Styled Components
const Wrapper = styled.div`
  padding: 20px;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const SettingsRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const PriceInput = styled(Input)`
  width: 100%;
  padding: 2px 4px;
`;

const CountySelect = styled.select`
  width: 100%;
  padding: 2px 4px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: inherit;
`;

const CommentInput = styled(Input)`
  width: 300px;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.foreground.error};
  padding: 2px 6px;

  &:hover {
    opacity: 0.7;
  }
`;

const EmptyState = styled.div`
  padding: 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

const MyTable = styled(Table)`
  margin-bottom: 16px;

  .table-row > * {
    min-width: 0;
  }
` as typeof Table;

const StyledButton = styled(Button)`
  padding: 8px 16px;
  white-space: nowrap;
` as typeof Button;

const HistoryBanner = styled.div`
  background: ${({ theme }) => theme.colors.background.highlight ?? '#e8f4fd'};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const HistoryBannerButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const HistoryPanel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 16px;
  max-height: 350px;
  max-width: 500px;
  overflow-y: auto;
`;

const HistoryItem = styled.div<{ $active?: boolean; $selected?: boolean }>`
  padding: 4px 8px;
  font-size: 13px;
  background: ${({ $active, $selected, theme }) =>
    $selected
      ? theme.colors.background.button
      : $active
        ? theme.colors.background.highlight
        : 'transparent'};
  color: ${({ $selected, theme }) =>
    $selected ? theme.colors.foreground.button : 'inherit'};
  border-radius: 2px;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const DuplicateWarning = styled.div`
  color: ${({ theme }) => theme.colors.foreground.error};
  font-weight: 500;
  margin-bottom: 8px;
`;
// #endregion

const toDateInputValue = toDateOnlyString;

const toDateStr = toDateOnlyString;

const todayStr = () => formatDate(new Date());

const isActiveNow = (activeFrom: Date | string, activeTo?: Date | string | null): boolean => {
  const today = todayStr();
  const from = toDateStr(activeFrom);
  if (from > today) return false;
  if (!activeTo) return true;
  return toDateStr(activeTo) >= today;
};

const isDeadVersion = (activeFrom: Date | string, activeTo?: Date | string | null): boolean => {
  if (!activeTo) return false;
  return toDateStr(activeFrom) > toDateStr(activeTo);
};

interface PriceFormItem {
  county: string;
  fromCountyPrice: number;
  withinCountyPrice: number | null;
}

interface FormValues {
  comment: string;
  activeFrom: string;
  activeTo: string;
  prices: PriceFormItem[];
}

interface PriceRow {
  fieldIndex: number;
  fieldId: string;
  county: string;
  fromCountyPrice: number;
  withinCountyPrice: number | null;
}

interface RenderProps {
  register: ReturnType<typeof useForm<FormValues>>['register'];
  remove: (index: number) => void;
  isReadOnly: boolean;
}

const toFormValues = (
  version: PurchasePriceListModel | undefined | null
): FormValues => ({
  comment: version?.comment ?? '',
  activeFrom: toDateInputValue(version?.activeFrom) || todayStr(),
  activeTo: toDateInputValue(version?.activeTo),
  prices:
    version?.prices.map((p) => ({
      county: p.county,
      fromCountyPrice: p.fromCountyPrice,
      withinCountyPrice: p.withinCountyPrice ?? null,
    })) ?? [],
});

interface PurchasePriceListPageProps {
  /** Auktionsbilarnas prislista — samma format som inköp, egen prislistetyp. */
  auction?: boolean;
  lookupVersionId?: number | null;
  onLookupHandled?: () => void;
  lookupElement?: React.ReactNode;
}

const PurchasePriceListPage: FC<PurchasePriceListPageProps> = ({ auction = false, lookupVersionId, onLookupHandled, lookupElement }) => {
  const queryClient = useQueryClient();
  const modalStack = useModalStack();

  const [showHistory, setShowHistory] = useState(false);
  const [viewingVersionId, setViewingVersionId] = useState<number | null>(null);

  // Handle external lookup from parent
  useEffect(() => {
    if (lookupVersionId != null && lookupVersionId > 0) {
      setViewingVersionId(lookupVersionId);
      setShowHistory(false);
      onLookupHandled?.();
    }
  }, [lookupVersionId, onLookupHandled]);

  // --- Queries ---
  const activeVersionQuery = usePriceListGetActivePurchaseVersion({ auction });
  const versionsQuery = usePriceListGetAllVersions({
    type: auction ? PriceListGetAllVersionsType.NUMBER_7 : PriceListGetAllVersionsType.NUMBER_2,
  });
  const versions = useMemo(() => versionsQuery.data ?? [], [versionsQuery.data]);

  const activeVersion = activeVersionQuery.data ?? null;

  const historicalVersionQuery = usePriceListGetPurchaseVersion(
    { id: viewingVersionId! },
    { query: { enabled: viewingVersionId != null } }
  );

  const isViewingHistory = viewingVersionId != null;
  const viewedVersion = historicalVersionQuery.data ?? null;

  // --- Mutations ---
  const saveMutation = usePriceListSavePurchaseVersion();
  const activateVersionMutation = usePriceListActivateVersion();
  const isMutating = saveMutation.isLoading || activateVersionMutation.isLoading;

  // --- Form ---
  const { register, control, watch, reset, handleSubmit, formState } =
    useForm<FormValues>({
      defaultValues: toFormValues(null),
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'prices',
  });

  const watchedActiveFrom = watch('activeFrom');
  const watchedActiveTo = watch('activeTo');
  const watchedPrices = useWatch({ control, name: 'prices' });

  // Sync form when active version data arrives
  const [lastSyncedId, setLastSyncedId] = useState<number | null>(null);
  useEffect(() => {
    if (activeVersion && activeVersion.id !== lastSyncedId && !isViewingHistory) {
      reset(toFormValues(activeVersion));
      setLastSyncedId(activeVersion.id);
    }
  }, [activeVersion, lastSyncedId, reset, isViewingHistory]);

  // Sync form when viewing a historical version
  useEffect(() => {
    if (viewedVersion) {
      reset(toFormValues(viewedVersion));
    }
  }, [viewedVersion, reset]);

  // --- Duplicate detection ---
  const duplicateIndices = useMemo(() => {
    const indices = new Set<number>();
    if (!watchedPrices) return indices;

    const seen = new Map<string, number[]>();
    watchedPrices.forEach((p, i) => {
      if (!p.county) return;
      const key = p.county.toUpperCase();
      const existing = seen.get(key);
      if (existing) {
        existing.push(i);
      } else {
        seen.set(key, [i]);
      }
    });

    for (const group of seen.values()) {
      if (group.length > 1) {
        for (const idx of group) {
          indices.add(idx);
        }
      }
    }
    return indices;
  }, [watchedPrices]);

  const hasDuplicates = duplicateIndices.size > 0;

  // --- Filter out "dead" versions (activeFrom > activeTo) ---
  const visibleVersions = useMemo(() =>
    versions.filter(v => !isDeadVersion(v.activeFrom, v.activeTo)),
    [versions]
  );

  const { isDirty } = formState;

  // --- Historical overlap detection ---
  // Mirrors backend HandleOverlaps: block if activeFrom < today and any existing
  // version with activeFrom < today overlaps with the new date range.
  const hasHistoricalOverlap = useMemo(() => {
    if (!isDirty || !watchedActiveFrom) return false;
    const today = todayStr();
    if (watchedActiveFrom >= today) return false;
    const newTo = watchedActiveTo || '9999-12-31';
    return visibleVersions.some(v => {
      const vFrom = toDateStr(v.activeFrom);
      if (vFrom >= today) return false;
      const vTo = toDateStr(v.activeTo) || '9999-12-31';
      return watchedActiveFrom <= vTo && vFrom <= newTo;
    });
  }, [visibleVersions, watchedActiveFrom, watchedActiveTo, isDirty]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // --- Invalidation helper ---
  const invalidateAll = async () => {
    await queryClient.invalidateQueries(
      getPriceListGetAllVersionsQueryKey()
    );
    await queryClient.invalidateQueries(
      getPriceListGetActivePurchaseVersionQueryKey({ auction })
    );
  };

  // --- Handlers ---
  const handleReturnToActive = () => {
    setViewingVersionId(null);
    setLastSyncedId(null);
  };

  const handleViewVersion = (versionId: number) => {
    const ver = versions.find((v) => v.id === versionId);
    if (ver && isActiveNow(ver.activeFrom, ver.activeTo)) {
      handleReturnToActive();
    } else {
      setViewingVersionId(versionId);
    }
  };

  const handleActivateVersion = () => {
    if (!viewedVersion) return;
    const label = `${toDateInputValue(viewedVersion.activeFrom)} → ${viewedVersion.activeTo ? toDateInputValue(viewedVersion.activeTo) : 'tills vidare'}`;
    modalStack.push(
      <ConfirmModal
        title="Aktivera version"
        content={`Är du säker på att du vill aktivera prislista ${label}? Den kommer aktiveras från och med nu.`}
        onConfirm={async () => {
          try {
            await activateVersionMutation.mutateAsync({ params: { id: viewedVersion.id } });
            await invalidateAll();
            setViewingVersionId(null);
            setLastSyncedId(null);
            toast.success('Prislistan har aktiverats');
          } catch (err) {
            showException(err);
          }
          modalStack.pop();
        }}
        onCancel={() => modalStack.pop()}
      />
    );
  };

  const doSave = async (data: FormValues) => {
    try {
      await saveMutation.mutateAsync({
        data: {
          comment: data.comment || undefined,
          activeFrom: new Date(data.activeFrom),
          activeTo: data.activeTo ? new Date(data.activeTo) : undefined,
          prices: data.prices.map((p) => ({
            county: p.county,
            fromCountyPrice: p.fromCountyPrice,
            withinCountyPrice: p.withinCountyPrice ?? undefined,
          })),
        },
        params: { auction },
      });
      await invalidateAll();
      setLastSyncedId(null);
      toast.success('Prislistan har sparats');
    } catch (err) {
      showException(err);
    }
  };

  const onSave = handleSubmit(async (data) => {
    modalStack.push(
      <ConfirmModal
        title="Spara ny version"
        content={data.activeFrom <= todayStr()
          ? 'Den nya versionen kommer att aktiveras automatiskt.'
          : `Den nya versionen kommer att aktiveras automatiskt fr.o.m ${data.activeFrom}.`}
        onConfirm={async () => {
          modalStack.pop();
          await doSave(data);
        }}
        onCancel={() => modalStack.pop()}
      />
    );
  });

  // --- Build table rows ---
  const rows: PriceRow[] = useMemo(() => {
    return fields.map((field, i) => ({
      fieldIndex: i,
      fieldId: field.id,
      county: watchedPrices?.[i]?.county ?? field.county,
      fromCountyPrice: watchedPrices?.[i]?.fromCountyPrice ?? 0,
      withinCountyPrice: watchedPrices?.[i]?.withinCountyPrice ?? null,
    }));
  }, [fields, watchedPrices]);

  // --- Row className ---
  const rowClassName = useCallback(
    (row: PriceRow) =>
      `row-${row.fieldIndex}${duplicateIndices.has(row.fieldIndex) ? ' duplicate-row' : ''}`,
    [duplicateIndices]
  );

  // --- Column settings ---
  const columnSettings = useMemo((): ColumnSetting<PriceRow, RenderProps>[] => {
    return [
      {
        head: '#',
        width: 15,
        render: (row) => `${row.fieldIndex + 1}`,
        sortFunction: (a, b) => a.fieldIndex - b.fieldIndex,
      },
      {
        head: 'Län',
        width: 80,
        focusable: true,
        render: (row, rp) =>
          rp.isReadOnly ? (
            row.county
          ) : (
            <CountySelect
              disabled={rp.isReadOnly}
              {...rp.register(`prices.${row.fieldIndex}.county`)}
            >
              <option value="">-- Välj län --</option>
              {SWEDISH_COUNTIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </CountySelect>
          ),
        sortFunction: (a, b) => a.county.localeCompare(b.county),
      },
      {
        head: 'Pris (från annat län)',
        width: 60,
        focusable: true,
        render: (row, rp) => (
          <PriceInput
            type="number"
            min={0}
            step={1}
            disabled={rp.isReadOnly}
            {...rp.register(`prices.${row.fieldIndex}.fromCountyPrice`, {
              valueAsNumber: true,
            })}
          />
        ),
        sortFunction: (a, b) => a.fromCountyPrice - b.fromCountyPrice,
      },
      {
        head: 'Pris (inom län)',
        width: 60,
        focusable: true,
        render: (row, rp) => (
          <PriceInput
            type="number"
            min={0}
            step={1}
            disabled={rp.isReadOnly}
            placeholder="-"
            {...rp.register(`prices.${row.fieldIndex}.withinCountyPrice`, {
              setValueAs: (v: string) => (v === '' || v === null || v === undefined ? null : Number(v)),
            })}
          />
        ),
        sortFunction: (a, b) => (a.withinCountyPrice ?? 0) - (b.withinCountyPrice ?? 0),
      },
      {
        head: '',
        width: 15,
        render: (row, rp) =>
          rp.isReadOnly ? null : (
            <DeleteButton
              type="button"
              onClick={() => rp.remove(row.fieldIndex)}
              title="Ta bort rad"
            >
              <FontAwesomeIcon icon={faTrash} />
            </DeleteButton>
          ),
      },
    ];
  }, []);

  const renderProps: RenderProps = useMemo(
    () => ({ register, remove, isReadOnly: isViewingHistory }),
    [register, remove, isViewingHistory]
  );

  // --- Loading state ---
  if (activeVersionQuery.isLoading) return <LoadingSpinner />;

  const canSave = isDirty && !hasDuplicates && !hasHistoricalOverlap && !isMutating && !isViewingHistory;

  return (
    <Wrapper>
      <style>{`.duplicate-row { background: #fff3cd !important; }`}</style>
      <TopBar>
        <StyledButton
          disabled={!canSave}
          onClick={canSave ? onSave : undefined}
        >
          <FontAwesomeIcon icon={faFloppyDisk} /> Spara
        </StyledButton>

        <StyledButton
          onClick={() => setShowHistory((v) => !v)}
        >
          <FontAwesomeIcon icon={faClockRotateLeft} />
          {' '}Historik
        </StyledButton>

        {lookupElement}

        {isMutating && <LoadingSpinner />}
      </TopBar>

      {showHistory && (
        <HistoryPanel>
          <strong>Versionshistorik</strong>
          {visibleVersions.length === 0 && <div>Inga versioner</div>}
          {visibleVersions.map((v) => {
            const active = isActiveNow(v.activeFrom, v.activeTo);
            return (
              <HistoryItem
                key={v.id}
                $active={active}
                $selected={viewingVersionId === v.id}
                onClick={() => handleViewVersion(v.id)}
              >
                #{v.id} {new Date(v.activeFrom).toLocaleDateString('sv-SE')}
                {v.activeTo ? ` → ${new Date(v.activeTo).toLocaleDateString('sv-SE')}` : ' → tills vidare'}
                {v.comment ? ` — ${v.comment}` : ''}
                {active ? ' (Aktiv)' : ''}
              </HistoryItem>
            );
          })}
        </HistoryPanel>
      )}

      {isViewingHistory && viewedVersion && !isActiveNow(viewedVersion.activeFrom, viewedVersion.activeTo) && (
        <HistoryBanner>
          <span>
            {historicalVersionQuery.isLoading
              ? 'Laddar version...'
              : `Du visar version #${viewedVersion?.id} — ${toDateInputValue(viewedVersion?.activeFrom)} → ${viewedVersion?.activeTo ? toDateInputValue(viewedVersion.activeTo) : 'tills vidare'}${viewedVersion?.comment ? ` — ${viewedVersion.comment}` : ''}.`}
          </span>
          <HistoryBannerButtons>
            <StyledButton onClick={handleReturnToActive}>
              Tillbaka till aktiv version
            </StyledButton>
            <StyledButton onClick={handleActivateVersion}>
              <FontAwesomeIcon icon={faBolt} /> Aktivera från nu
            </StyledButton>
          </HistoryBannerButtons>
        </HistoryBanner>
      )}

      <SettingsRow>
        <LabelWrap label="Aktiv från">
          <Input
            type="date"
            width={150}
            disabled={isViewingHistory}
            {...register('activeFrom')}
          />
        </LabelWrap>

        <LabelWrap label="Aktiv till">
          <Input
            type="date"
            width={150}
            disabled={isViewingHistory}
            {...register('activeTo')}
          />
        </LabelWrap>

        <LabelWrap label="Kommentar">
          <CommentInput type="text" disabled={isViewingHistory} {...register('comment')} />
        </LabelWrap>
      </SettingsRow>

      {hasDuplicates && (
        <DuplicateWarning>
          Duplicerade län hittades. Varje län får bara finnas en gång.
        </DuplicateWarning>
      )}

      {hasHistoricalOverlap && (
        <DuplicateWarning>
          Den valda perioden överlappar med en historisk prislista. Ändra fråndatum.
        </DuplicateWarning>
      )}

      {rows.length === 0 ? (
        <EmptyState>
          {fields.length === 0
            ? 'Inga priser. Klicka "Lägg till rad" för att börja.'
            : 'Inga priser att visa.'}
        </EmptyState>
      ) : (
        <MyTable
          fluid
          columnSettings={columnSettings}
          renderProps={renderProps}
          rows={rows}
          useColumnWidthAsFlexAmount
          rowClassName={rowClassName}
        />
      )}

      {!isViewingHistory && (
        <StyledButton
          type="button"
          onClick={() =>
            append({
              county: '',
              fromCountyPrice: 0,
              withinCountyPrice: null,
            })
          }
        >
          <FontAwesomeIcon icon={faPlus} /> Lägg till rad
        </StyledButton>
      )}
    </Wrapper>
  );
};

export default PurchasePriceListPage;
